import type NumberUnit from '@exodus/currency';
import type { Logger } from '@exodus/logger';
import sendValidation from '@exodus/send-validation';
import {
  VALIDATION_TYPES,
  PRIORITY_LEVELS,
} from '@exodus/send-validation/src/constants';
import BASE_ASSET_INSUFFICIENT_FUNDS from '@exodus/send-validation/src/validations/BASE_ASSET_INSUFFICIENT_FUNDS';
import INSUFFICIENT_FUNDS from '@exodus/send-validation/src/validations/INSUFFICIENT_FUNDS';
import ZERO_AMOUNT from '@exodus/send-validation/src/validations/ZERO_AMOUNT';
import type { JSX } from 'react';

import type { FeesResult } from '@/types';
import type { IsBuySupportedByAssetFn } from '@/ui/hooks/use-is-buy-supported-by-asset-fn.js';
import { CAN_SEND_ZERO_AMOUNT } from './constants.js';

export type FormValidation<T> = {
  id: string;
  type: typeof VALIDATION_TYPES;
  shouldValidate?: (dependencies: T) => boolean;
  isValid: (dependencies: T) => boolean;
  priority?: number;
  getMessage?: (dependencies: T) => string;
  getComponent?: (props: Record<string, unknown>) => JSX.Element;
  getTopComponent?: (props: Record<string, unknown>) => JSX.Element;
  getAction?: (dependencies: T) => {
    label: string;
    navigateTo: string;
    navigateToState?: Record<string, unknown>;
  };
};

export type FailedFormValidation<T> = FormValidation<T> & {
  message: string;
  bottomValidationComponent: JSX.Element;
  topValidationComponent: JSX.Element;
};

export type FormValidationHook<T> = (dependencies: T) => {
  hasError: boolean;
  failedValidation?: FailedFormValidation<T>;
};

export type ZeroAmountValidationDependencies = {
  asset: any;
  amount?: NumberUnit | null;
};

export type SendValidationDependencies = ZeroAmountValidationDependencies &
  InsufficientFundsValidationDependencies &
  BaseAssetInsufficientFundsValidationDependencies & {
    destinationAddress: string;
    addressDetails: Record<string, never>;
    fees?: FeesResult;
  };

export const zeroAmountValidation = {
  ...ZERO_AMOUNT,
  shouldValidate: ({ asset, amount }) =>
    amount !== undefined &&
    amount !== null &&
    !CAN_SEND_ZERO_AMOUNT.includes(asset.name),
  isValid: ({ amount, ...deps }) =>
    ZERO_AMOUNT.isValid({ ...deps, sendAmount: amount }),
  getMessage: () => 'Amount must be larger than 0.',
} as FormValidation<ZeroAmountValidationDependencies>;

export const bitcoinCPFPValidation = {
  id: 'BITCOIN_CPFP_WARN',
  type: VALIDATION_TYPES.WARN,
  priority: PRIORITY_LEVELS.BASE,
  shouldValidate: ({ asset, fees }) =>
    asset.name === 'bitcoin' &&
    fees?.extraFeeData?.extraFee &&
    (fees?.extraFeeData?.type === 'cpfp' ||
      fees?.extraFeeData?.type === 'cpfpdust'),
  isValid: ({ fees }) => fees?.extraFeeData?.extraFee?.isZero,
  getMessage: () =>
    'To avoid extra fees, wait until your pending transactions are complete (CPFP).',
} as FormValidation<SendValidationDependencies>;

export type InsufficientFundsValidationDependencies = {
  availableBalance?: NumberUnit;
  amount?: NumberUnit | null;
  fundsLoading?: boolean;
};
export const insufficientFundsValidation = {
  ...INSUFFICIENT_FUNDS,
  shouldValidate: ({ availableBalance, amount, fundsLoading = false }) =>
    availableBalance && !amount?.isZero && !fundsLoading,
  isValid: ({ amount, ...deps }) =>
    INSUFFICIENT_FUNDS.isValid({ ...deps, sendAmount: amount }),
  getMessage: () => 'You have insufficient funds for this action.',
} as FormValidation<InsufficientFundsValidationDependencies>;

export type BaseAssetInsufficientFundsValidationDependencies = {
  asset: any;
  isFeeAssetEnough: boolean;
  isBuySupportedByAsset: IsBuySupportedByAssetFn;
};
export const baseAssetInsufficientFundsValidation = {
  ...BASE_ASSET_INSUFFICIENT_FUNDS,
  getMessage: ({ asset }) =>
    `You have insufficient ${asset.baseAsset.displayTicker} to pay for this transaction’s fee.`,
  getAction: ({ asset, isBuySupportedByAsset }) => {
    if (isBuySupportedByAsset(asset.feeAsset.name).value) {
      return {
        label: `Buy ${asset.feeAsset.displayTicker}`,
        navigateTo: `/buy`,
        navigateToState: { token: asset?.feeAsset?.name },
      };
    }
    return undefined;
  },
} as FormValidation<BaseAssetInsufficientFundsValidationDependencies>;

const returnNull = () => null;
const returnEmptyString = () => '';

export const createValidationHookWithDefaults = <T>({
  validations,
  logger,
}: {
  validations: FormValidation<T>[];
  logger?: Logger;
}) =>
  sendValidation.createAsyncValidationHook({
    validations: validations.map((validation) => ({
      ...validation,
      priority: validation.priority ?? PRIORITY_LEVELS.BASE,
      shouldValidate: validation.shouldValidate ?? true,
      getComponent: validation.getComponent ?? returnNull,
      getTopComponent: validation.getTopComponent ?? returnNull,
      getMessage: validation.getMessage ?? returnEmptyString,
    })),
    logger,
  }) as FormValidationHook<T>;
